import type { NavItem, NavGroup } from '@/shared/schemas';
export type { NavItem, NavGroup };

export interface SubTabItem {
    title: string;
    href: string;
    icon?: string;
    roles?: string[];
    moduleId?: string;
    badge?: string;
}

export interface DomainItem {
    id: string; // e.g. 'sales', 'finance', 'production', 'warehouse', 'engineering', 'system'
    title: string;
    icon: string;
    section: string; // e.g. 'Tổng quan', 'Bán hàng & Thương mại', 'Kế hoạch & Sản xuất', 'Kho vận & Cung ứng', 'Tài chính & Kế toán', 'Hệ thống & Quản trị'
    defaultHref: string;
    roles?: string[];
    tabs: SubTabItem[];
}

export const DUAL_AXIS_NAVIGATION: DomainItem[] = [
    // ═══════════════════════════════════════
    // I. TỔNG QUAN (Dashboard & Executive)
    // ═══════════════════════════════════════
    {
        id: "dashboard",
        title: "Dashboard",
        section: "Tổng quan",
        icon: "LayoutDashboard",
        defaultHref: "/",
        tabs: [
            {
                title: "Dashboard",
                href: "/",
                icon: "LayoutDashboard"
            }
        ]
    },
    {
        id: "reports",
        title: "Báo Cáo Quản Trị",
        section: "Tổng quan",
        icon: "FileSpreadsheet",
        defaultHref: "/reports",
        tabs: [
            {
                title: "Báo Cáo Quản Trị",
                href: "/reports",
                icon: "FileSpreadsheet",
                moduleId: "reports"
            }
        ]
    },

    // ═══════════════════════════════════════
    // II. BÁN HÀNG & THƯƠNG MẠI (Sales & CRM - 6 tabs)
    // ═══════════════════════════════════════
    {
        id: "sales",
        title: "Kinh doanh",
        section: "Bán hàng & Thương mại",
        icon: "ShoppingCart",
        defaultHref: "/quotations",
        tabs: [
            {
                title: "Báo giá",
                href: "/quotations",
                icon: "FileText",
                moduleId: "quotations"
            },
            {
                title: "Đơn hàng",
                href: "/orders",
                icon: "ShoppingCart",
                moduleId: "orders"
            },
            {
                title: "ĐH Gia công",
                href: "/outsource-orders",
                icon: "Factory",
                moduleId: "outsource-orders"
            },
            {
                title: "Giao hàng",
                href: "/delivery",
                icon: "Truck",
                moduleId: "deliveries"
            },
            {
                title: "Khách hàng",
                href: "/customers",
                icon: "Users2",
                moduleId: "customers"
            },
            {
                title: "Sản phẩm",
                href: "/products",
                icon: "Package",
                moduleId: "products"
            },
            {
                title: "Loại sản phẩm",
                href: "/product-types",
                icon: "Layers",
                moduleId: "product-types"
            }
        ]
    },

    // ═══════════════════════════════════════
    // III. KẾ HOẠCH & SẢN XUẤT (Production & Manufacturing - Ngay dưới Bán hàng)
    // ═══════════════════════════════════════
    // 1. Kế Hoạch SX
    {
        id: "production_planning",
        title: "Kế hoạch",
        section: "Kế hoạch & Sản xuất",
        icon: "ClipboardList",
        defaultHref: "/production-orders",
        tabs: [
            {
                title: "Bình file Prepress",
                href: "/layouts",
                icon: "Layers",
                moduleId: "layouts"
            },
            {
                title: "Lệnh sản xuất",
                href: "/production-orders",
                icon: "ClipboardList",
                moduleId: "production-orders"
            },
            {
                title: "Kế hoạch Vật tư",
                href: "/material-planning",
                icon: "Box",
                moduleId: "material-planning"
            },
            {
                title: "Tiến Độ Công Đoạn",
                href: "/production-orders?view=tracking",
                icon: "Layers",
                moduleId: "production-orders"
            }
        ]
    },

    // 2. Xưởng SX (Shopfloor Execution)
    {
        id: "shopfloor",
        title: "Xưởng sản xuất",
        section: "Kế hoạch & Sản xuất",
        icon: "Factory",
        defaultHref: "/process-planning",
        tabs: [
            {
                title: "Kế hoạch sản xuất",
                href: "/process-planning",
                icon: "Calendar",
                moduleId: "process-planning"
            },
            {
                title: "Nhật ký sản xuất",
                href: "/factory/production-logs",
                icon: "ClipboardList",
                moduleId: "machine-logs"
            },
            {
                title: "Đóng Gói & Nhập Kho",
                href: "/factory/packing",
                icon: "PackageCheck",
                moduleId: "qc-final"
            },
            {
                title: "Sự cố thiết bị",
                href: "/mes/incidents",
                icon: "AlertTriangle",
                moduleId: "machines"
            },
            {
                title: "Bảo trì bảo dưỡng",
                href: "/mes/maintenance",
                icon: "Wrench",
                moduleId: "machines"
            },
            {
                title: "Thiết bị SX",
                href: "/machines",
                icon: "Settings",
                moduleId: "machines"
            }
        ]
    },

    // 3. Kỹ Thuật (Engineering, Tooling & Plates)
    {
        id: "engineering",
        title: "Kỹ thuật",
        section: "Kế hoạch & Sản xuất",
        icon: "Layers",
        defaultHref: "/designs",
        tabs: [
            {
                title: "Artwork Thiết kế",
                href: "/designs",
                icon: "PenTool",
                moduleId: "specs"
            },
            {
                title: "Kẽm CTP & Log",
                href: "/plates",
                icon: "Printer",
                moduleId: "plates"
            },
            {
                title: "Quản lý Khuôn",
                href: "/molds",
                icon: "Scissors",
                moduleId: "molds"
            },
            {
                title: "Tiêu chuẩn SP",
                href: "/specs",
                icon: "FileText",
                moduleId: "specs"
            },
            {
                title: "Kho Kỹ thuật",
                href: "/warehouse/tech",
                icon: "Wrench",
                moduleId: "material-warehouse"
            }
        ]
    },

    // 5. Chất Lượng QC (Quality & KCS)
    {
        id: "quality",
        title: "Chất lượng",
        section: "Kế hoạch & Sản xuất",
        icon: "CheckCircle2",
        defaultHref: "/qc-materials",
        tabs: [
            {
                title: "IQC Đầu vào",
                href: "/qc-materials",
                icon: "CheckCircle2",
                moduleId: "qc-materials"
            },
            {
                title: "IPQC Công đoạn",
                href: "/qc-process",
                icon: "Microscope",
                moduleId: "qc-process"
            },
            {
                title: "FQC Thành phẩm",
                href: "/qc-final",
                icon: "PackageCheck",
                moduleId: "qc-final"
            },
            {
                title: "Sự cố NCR",
                href: "/ncr",
                icon: "AlertTriangle",
                moduleId: "ncr"
            },
            {
                title: "Biểu mẫu QC",
                href: "/qc-templates",
                icon: "FileSpreadsheet",
                moduleId: "qc-final"
            }
        ]
    },

    // ═══════════════════════════════════════
    // IV. KHO VẬN & CUNG ỨNG (Supply Chain & Warehouses)
    // ═══════════════════════════════════════
    // 1. Mua Hàng & Cung Ứng
    {
        id: "procurement",
        title: "Mua hàng",
        section: "Kho vận & Cung ứng",
        icon: "Briefcase",
        defaultHref: "/purchasing",
        tabs: [
            {
                title: "ĐH Mua hàng",
                href: "/purchasing",
                icon: "CreditCard",
                moduleId: "purchasing"
            },
            {
                title: "Nhà Cung cấp",
                href: "/vendors",
                icon: "Users2",
                moduleId: "vendors"
            },
            {
                title: "Danh mục Vật tư",
                href: "/materials?domain=procurement",
                icon: "Box",
                moduleId: "materials"
            },
            {
                title: "Loại vật tư",
                href: "/material-types",
                icon: "Layers",
                moduleId: "material-types"
            }
        ]
    },

    // 2. Kho
    {
        id: "warehouse",
        title: "Kho",
        section: "Kho vận & Cung ứng",
        icon: "Warehouse",
        defaultHref: "/warehouse/material/transactions",
        // Mỗi kho một mục (vật tư / thành phẩm / kỹ thuật — không quản kho BTP, phế liệu); mỗi kho đủ Phiếu nhập xuất, Báo cáo NXT, Sổ kho, Kiểm kê
        tabs: [
            {
                title: "Kho Vật tư",
                href: "/warehouse/material/transactions",
                icon: "Box",
                moduleId: "material-warehouse"
            },
            {
                title: "Kho Thành phẩm",
                href: "/warehouse/fg/transactions",
                icon: "Package",
                moduleId: "fg-inventory"
            },
            {
                title: "Kho Kỹ thuật",
                href: "/warehouse/tech/transactions",
                icon: "Wrench",
                moduleId: "material-warehouse"
            },
            {
                title: "Danh mục Kho",
                href: "/warehouse/master-data",
                icon: "Warehouse",
                moduleId: "material-warehouse"
            },
            {
                title: "Mã & Tồn NVL",
                href: "/materials",
                icon: "Box",
                moduleId: "materials"
            }
        ]
    },

    // ═══════════════════════════════════════
    // V. TÀI CHÍNH & KẾ TOÁN (Finance & Accounting)
    // ═══════════════════════════════════════
    {
        id: "finance",
        title: "Tài Chính",
        section: "Tài chính & Kế toán",
        icon: "CreditCard",
        defaultHref: "/finance/receipts",
        tabs: [
            {
                title: "Thu chi & Sổ quỹ",
                href: "/finance/receipts",
                icon: "FileText",
                moduleId: "receipts"
            },
            {
                title: "Công nợ KH",
                href: "/debts",
                icon: "PieChart",
                moduleId: "debts"
            },
            {
                title: "Công nợ NCC",
                href: "/purchasing/debts",
                icon: "PieChart",
                moduleId: "debts"
            },
            {
                title: "Đối soát công nợ",
                href: "/finance/reconciliations",
                icon: "ClipboardCheck",
                moduleId: "debts"
            },
            {
                title: "Báo cáo Bán hàng",
                href: "/reports/sales",
                icon: "BarChart",
                moduleId: "reports"
            }
        ]
    },

    // ═══════════════════════════════════════
    // VI. HỆ THỐNG & QUẢN TRỊ (System, Master Data & Governance)
    // ═══════════════════════════════════════
    // 1. Hệ Thống & Quy Trình (Bao gồm Quản trị Quy trình & Nhân sự Phân quyền)
    {
        id: "system_process",
        title: "Hệ Thống & Quy Trình",
        section: "Hệ thống & Quản trị",
        icon: "Settings",
        defaultHref: "/processes",
        tabs: [
            {
                title: "Quy trình SX",
                href: "/processes",
                icon: "Activity",
                moduleId: "processes"
            },
            {
                title: "Ma trận Hao hụt",
                href: "/waste-matrix",
                icon: "Percent",
                moduleId: "processes"
            },
            {
                title: "Workflow Duyệt",
                href: "/workflows",
                icon: "GitBranch",
                moduleId: "workflows"
            },
            {
                title: "Nhân sự",
                href: "/users",
                icon: "Users",
                moduleId: "users"
            },
            {
                title: "Phân quyền",
                href: "/roles",
                icon: "ShieldCheck",
                moduleId: "roles"
            },
            {
                title: "Thông tin Cty",
                href: "/company-info",
                icon: "Briefcase",
                moduleId: "company-info"
            },
            {
                title: "Cấu hình",
                href: "/configs",
                icon: "Database",
                moduleId: "configs"
            }
        ]
    },

    // 2. Hỗ Trợ
    {
        id: "support",
        title: "Hỗ Trợ",
        section: "Hệ thống & Quản trị",
        icon: "HelpCircle",
        defaultHref: "/guide",
        tabs: [
            {
                title: "Lịch sử hệ thống",
                href: "/system/audit-logs",
                icon: "History",
                moduleId: "roles"
            },
            {
                title: "Nhật ký sự kiện",
                href: "/system/user-events",
                icon: "Activity",
                moduleId: "roles"
            },
            {
                title: "Thùng rác",
                href: "/trash",
                icon: "Trash2",
                moduleId: "trash"
            },
            {
                title: "Nhật ký Release",
                href: "/changelog",
                icon: "Sparkles"
            },
            {
                title: "Phản hồi hệ thống",
                href: "/feedback",
                icon: "MessageSquarePlus",
                moduleId: "feedback"
            },
            {
                title: "Hướng dẫn sử dụng",
                href: "/guide",
                icon: "BookOpen"
            }
        ]
    }
];

const LEGACY_PATH_ALIASES: Record<string, string> = {
    "/deliveries": "/delivery",
    "/purchase-orders": "/purchasing",
    "/production/prepress": "/layouts",
    "/production/print-layouts": "/layouts",
    "/production/ctp-tracking": "/plates",
    "/plates/ctp-logs": "/plates",
    "/master/tooling": "/molds",
    "/master": "/product-types",
    "/sales/debts": "/debts",
    "/finance/ar/reconciliations": "/finance/reconciliations",
    "/finance/ap/reconciliations": "/finance/reconciliations",
    "/finance/ar-aging": "/debts",
    "/finance/ar": "/debts",
    "/finance/ap": "/purchasing/debts",
    "/finance": "/finance/receipts",
    "/quality/materials": "/qc-materials",
    "/quality/iqc": "/qc-materials",
    "/qc-check": "/qc-process",
    "/qc/reports": "/qc-final",
    "/quality/reports": "/qc-final",
    "/wip-transfers": "/process-planning",
    "/wip-transfer": "/process-planning",
    "/mes/oee": "/mes/incidents",
    "/audit-logs": "/system/audit-logs",
    "/planning/paper-allocation": "/material-planning",
    "/planning/mrp": "/material-planning",
    "/material-supply-board": "/material-planning",
    "/production-logs": "/factory/production-logs",
    "/shopfloor/loss-time-analytics": "/factory/production-logs",
    "/mes/andon": "/factory/production-logs",
    "/mes/touch-terminal": "/factory/production-logs",
    "/mes": "/factory/production-logs",
    "/production-scheduling": "/production-orders",
    "/shopfloor/terminal": "/factory/production-logs",
    "/tooling-tracking-board": "/molds",
    "/machine-logs": "/factory/production-logs",
    "/outsource-orders/tracking": "/outsource-orders",
    "/outsource-orders/tracking-hub": "/outsource-orders",
    "/production-orders/matrix": "/production-orders",
    "/production-orders/prepress-queue": "/production-orders",
    "/production-orders/prepress-wizard": "/production-orders",
    "/production-orders/tracking": "/production-orders",
    "/warehouse/inventory-nxt": "/warehouse/fg/stock-card",
    "/warehouse/stocktake": "/warehouse/fg/stocktakes",
};

/**
 * Smart matching algorithm to find current active Domain and SubTab
 */
export function getActiveDomainAndTab(pathname: string, searchParamsString: string = ""): {
    activeDomain: DomainItem;
    activeTab: SubTabItem | null;
} {
    let cleanPath = pathname.replace(/\/$/, '') || '/';
    
    // Nếu đường dẫn trùng chính xác với 1 tab đã đăng ký (vd: /mes/maintenance, /mes/oee)
    // thì KHÔNG áp alias tiền tố (vd: "/mes" -> nhật ký vận hành) để tránh hiển thị sai tiêu đề.
    const hasExactTab = DUAL_AXIS_NAVIGATION.some(d => d.tabs.some(t => t.href === cleanPath));

    // Resolve legacy route aliases to consolidated parents
    if (!hasExactTab) {
        for (const [legacyPrefix, targetPath] of Object.entries(LEGACY_PATH_ALIASES)) {
            if (cleanPath === legacyPrefix || cleanPath.startsWith(legacyPrefix + '/')) {
                cleanPath = targetPath;
                break;
            }
        }
    }

    const fullUrlWithSearch = searchParamsString ? `${cleanPath}${searchParamsString}` : cleanPath;

    // 0. Shopfloor domain priority: /process-planning and /factory/process-planning belong to Shopfloor domain (Tab 1)
    if (cleanPath === '/process-planning' || cleanPath.startsWith('/factory/process-planning')) {
        const sfDomain = DUAL_AXIS_NAVIGATION.find(d => d.id === 'shopfloor');
        if (sfDomain) {
            return {
                activeDomain: sfDomain,
                activeTab: sfDomain.tabs[0],
            };
        }
    }

    // 0.5. Contextual routing for /materials between Procurement and Warehouse
    if (cleanPath === '/materials' || cleanPath.startsWith('/materials/')) {
        if (searchParamsString.includes('domain=procurement')) {
            const procDomain = DUAL_AXIS_NAVIGATION.find(d => d.id === 'procurement');
            if (procDomain) {
                const matTab = procDomain.tabs.find(t => t.href.startsWith('/materials')) || procDomain.tabs[0];
                return { activeDomain: procDomain, activeTab: matTab };
            }
        } else {
            const whDomain = DUAL_AXIS_NAVIGATION.find(d => d.id === 'warehouse');
            if (whDomain) {
                const matTab = whDomain.tabs.find(t => t.href.startsWith('/materials')) || whDomain.tabs[0];
                return { activeDomain: whDomain, activeTab: matTab };
            }
        }
    }

    // 0.6. Mọi trang trong một kho (/warehouse/{loại}/…: Báo cáo NXT, Sổ kho, Kiểm kê) thuộc tab của đúng kho đó.
    // Tab kho trỏ tới /warehouse/{loại}/transactions nên khớp tiền tố không bắt được /warehouse/fg (trước đây sáng nhầm "Kho Vật tư").
    const whTypeMatch = cleanPath.match(/^\/warehouse\/([^/]+)(?:\/|$)/);
    if (whTypeMatch) {
        for (const domain of DUAL_AXIS_NAVIGATION) {
            const tab = domain.tabs.find(t => t.href.startsWith(`/warehouse/${whTypeMatch[1]}/`) || t.href === `/warehouse/${whTypeMatch[1]}`);
            if (tab) return { activeDomain: domain, activeTab: tab };
        }
    }

    // 1. Try exact match (including query string if present)
    for (const domain of DUAL_AXIS_NAVIGATION) {
        for (const tab of domain.tabs) {
            if (tab.href === fullUrlWithSearch || tab.href === cleanPath) {
                return { activeDomain: domain, activeTab: tab };
            }
        }
    }

    // 2. Try prefix match (longest prefix match)
    let bestDomain: DomainItem = DUAL_AXIS_NAVIGATION[0];
    let bestTab: SubTabItem | null = null;
    let bestMatchLen = 0;

    for (const domain of DUAL_AXIS_NAVIGATION) {
        for (const tab of domain.tabs) {
            const basePath = tab.href.split('?')[0];
            if (basePath !== '/' && (cleanPath === basePath || cleanPath.startsWith(basePath + '/'))) {
                if (basePath.length > bestMatchLen) {
                    bestMatchLen = basePath.length;
                    bestDomain = domain;
                    bestTab = tab;
                }
            }
        }
    }

    if (bestMatchLen > 0) {
        return { activeDomain: bestDomain, activeTab: bestTab };
    }

    // 3. Reports sub-routes protection: always anchor to Báo Cáo Quản Trị
    if (cleanPath.startsWith('/reports')) {
        const reportsDomain = DUAL_AXIS_NAVIGATION.find(d => d.id === 'reports') || DUAL_AXIS_NAVIGATION[0];
        const reportTab = reportsDomain.tabs.find(t => t.href === '/reports') || reportsDomain.tabs[0];
        return {
            activeDomain: reportsDomain,
            activeTab: reportTab,
        };
    }

    // 4. Warehouse domain protection: NEVER fallback warehouse routes to Dashboard!
    if (cleanPath.startsWith('/warehouse') || cleanPath.startsWith('/inventory') || cleanPath === '/materials') {
        // Miền Kho có id 'warehouse' (trước đây tìm id không tồn tại → rơi vào miền đầu nhóm Kho vận = Mua hàng,
        // trang Kiểm kê sáng nhầm menu "ĐH Mua hàng")
        const whDomain = DUAL_AXIS_NAVIGATION.find(d => d.id === 'warehouse') || DUAL_AXIS_NAVIGATION.find(d => d.section === 'Kho vận & Cung ứng') || DUAL_AXIS_NAVIGATION[0];
        if (whDomain) {
            // /warehouse/<loại kho>/... (kiểm kê, sổ kho...) → tab cùng loại kho
            const typePrefix = cleanPath.match(/^\/warehouse\/[^/]+\//)?.[0];
            const matchedTab =
                whDomain.tabs.find(t => cleanPath === t.href || cleanPath.startsWith(t.href + '/')) ||
                (typePrefix ? whDomain.tabs.find(t => t.href.startsWith(typePrefix)) : undefined) ||
                whDomain.tabs[0];
            return {
                activeDomain: whDomain,
                activeTab: matchedTab,
            };
        }
    }

    // Fallback for Dashboard /
    if (cleanPath === '/') {
        return {
            activeDomain: DUAL_AXIS_NAVIGATION[0],
            activeTab: DUAL_AXIS_NAVIGATION[0].tabs[0]
        };
    }

    return {
        activeDomain: DUAL_AXIS_NAVIGATION[0],
        activeTab: DUAL_AXIS_NAVIGATION[0].tabs[0]
    };
}

// ═══════════════════════════════════════
// BACKWARD COMPATIBILITY FOR NAV_ITEMS
// ═══════════════════════════════════════
export const NAV_ITEMS: (NavItem | NavGroup)[] = DUAL_AXIS_NAVIGATION.map(domain => ({
    title: domain.title,
    icon: domain.icon,
    roles: domain.roles,
    items: domain.tabs.map(tab => ({
        title: tab.title,
        href: tab.href,
        icon: tab.icon || "FileText",
        roles: tab.roles
    }))
}));
